/**
 * School Session Setup — v2.2.1 Session Day Counter & Jewish Holiday Reference
 * Configure first/last day, calendar off days, daily schedule; session day count; Jewish holiday reference.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Collapse,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AppHeader from './AppHeader';
import { sessionAPI } from '../services/api';
import { format, parseISO, getDay, addDays, isBefore, isAfter, startOfMonth, endOfMonth, eachDayOfInterval, getDate } from 'date-fns';

const MIN_SESSION_DAYS = 180;
const HEBCAL_HOLIDAY_KEYS = [
  'rosh hashana', 'yom kippur', 'sukkot', 'sukkos', 'shemini atzeret', 'simchat torah', 'chanukah',
  'tu bishvat', 'purim', 'ta\'anit esther', 'taanis esther', 'pesach', 'passover',
  'yom hashoah', 'yom hazikaron', 'yom haatzmaut', 'lag baomer', 'shavuot', 'tisha b\'av',
];

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const OFF_DAY_STATUSES = [
  { value: 'no_sessions', label: 'No Sessions' },
  { value: 'friday_dismissal', label: 'Friday / Early Dismissal' },
  { value: 'schedule_to_follow', label: 'Schedule to Follow' },
  { value: 'tentative_no_sessions', label: 'Tentative No Sessions' },
];

const formatTime = (t) => {
  if (!t) return '';
  if (typeof t === 'string') return t.slice(0, 5);
  return `${String(t.hours ?? t.slice(0, 2)).padStart(2, '0')}:${String(t.minutes ?? t.slice(3, 5)).padStart(2, '0')}`;
};
const toTimeValue = (t) => (t ? formatTime(t) : '');

/** 0=Sun, 1=Mon, ..., 6=Sat. Weekday = Mon–Fri */
const isWeekday = (dateStr) => {
  if (!dateStr) return false;
  const d = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
  const day = getDay(d);
  return day >= 1 && day <= 5;
};

/** Count Mon–Fri between start and end (inclusive). */
function countWeekdaysInRange(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return 0;
  const start = parseISO(startDateStr);
  const end = parseISO(endDateStr);
  if (isAfter(start, end)) return 0;
  let count = 0;
  let d = start;
  while (isBefore(d, end) || d.getTime() === end.getTime()) {
    if (isWeekday(d)) count += 1;
    d = addDays(d, 1);
  }
  return count;
}

export default function SchoolSessionSetup() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);

  // Off day modal
  const [offDayModal, setOffDayModal] = useState({ open: false, edit: null });
  const [offDayForm, setOffDayForm] = useState({ date: '', reason: '', status: 'no_sessions', dismissal_time: '' });

  // Jewish holiday reference (Hebcal)
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidaysError, setHolidaysError] = useState(null);
  const [holidayPanelOpen, setHolidayPanelOpen] = useState(true);

  const loadSites = useCallback(async () => {
    try {
      const res = await sessionAPI.getSites();
      const list = res.data?.results ?? res.data ?? [];
      setSites(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || 'Failed to load sites');
    }
  }, []);

  const loadSessions = useCallback(async () => {
    if (!siteId) {
      setSessions([]);
      return;
    }
    setError('');
    try {
      const res = await sessionAPI.getSessions({ site: siteId });
      const list = res.data?.results ?? res.data ?? [];
      setSessions(Array.isArray(list) ? list : []);
      if (!sessionId || !list.some((s) => s.id === Number(sessionId))) {
        setSessionId('');
        setSession(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load sessions');
      setSessions([]);
    }
  }, [siteId, sessionId]);

  const loadSession = useCallback(async () => {
    if (!sessionId) {
      setSession(null);
      return;
    }
    setLoadingSession(true);
    setError('');
    try {
      const res = await sessionAPI.getSession(sessionId);
      setSession(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load session');
      setSession(null);
    } finally {
      setLoadingSession(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const sessionDayCount = useMemo(() => {
    if (!session?.start_date || !session?.end_date) return null;
    const totalWeekdays = countWeekdaysInRange(session.start_date, session.end_date);
    const start = parseISO(session.start_date);
    const end = parseISO(session.end_date);
    const inRange = (dateStr) => {
      const day = parseISO(dateStr);
      return (isBefore(day, start) || isAfter(day, end)) ? false : true;
    };
    const weekdayOffDays = (session.off_days ?? []).filter((o) => o.date && isWeekday(o.date) && inRange(o.date));
    const sessionDays = totalWeekdays - weekdayOffDays.length;
    return { total: sessionDays, diff: sessionDays - MIN_SESSION_DAYS };
  }, [session?.start_date, session?.end_date, session?.off_days]);

  useEffect(() => {
    if (!session?.start_date || !session?.end_date) {
      setHolidays([]);
      setHolidaysError(null);
      return;
    }
    let cancelled = false;
    setHolidaysLoading(true);
    setHolidaysError(null);
    const start = session.start_date;
    const end = session.end_date;
    sessionAPI
      .getJewishHolidays(start, end)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const items = (data.items || []).filter((i) => i.category === 'holiday');
        const includeTitle = (title) => {
          const t = (title || '').toLowerCase();
          return HEBCAL_HOLIDAY_KEYS.some((k) => t.includes(k));
        };
        const withDate = items
          .filter((i) => includeTitle(i.title))
          .map((i) => {
            const raw = (i.date || '').split('T')[0];
            const dateStr = raw ? format(parseISO(raw), 'yyyy-MM-dd') : '';
            return { ...i, dateStr, isWeekday: dateStr ? isWeekday(dateStr) : false };
          })
          .filter((i) => i.dateStr);
        setHolidays(withDate);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.detail || err.message || 'Holiday data unavailable — please check back later.';
          setHolidaysError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setHolidaysLoading(false);
      });
    return () => { cancelled = true; };
  }, [session?.start_date, session?.end_date]);

  const handleSaveSession = async () => {
    if (!session) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: session.name,
        start_date: session.start_date,
        end_date: session.end_date,
        first_day_school: session.first_day_school || null,
        last_day_school: session.last_day_school || null,
        back_to_school_night_date: session.back_to_school_night_date || null,
        pta_evening_date: session.pta_evening_date || null,
      };
      await sessionAPI.updateSession(session.id, payload);
      setSession((prev) => ({ ...prev, ...payload }));
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSessionField = (field, value) => {
    setSession((prev) => (prev ? { ...prev, [field]: value || null } : prev));
  };

  const openOffDayModal = (edit = null) => {
    if (edit) {
      setOffDayForm({
        date: edit.date,
        reason: edit.reason || '',
        status: edit.status || 'no_sessions',
        dismissal_time: toTimeValue(edit.dismissal_time),
      });
      setOffDayModal({ open: true, edit });
    } else {
      setOffDayForm({ date: '', reason: '', status: 'no_sessions', dismissal_time: '' });
      setOffDayModal({ open: true, edit: null });
    }
  };

  const closeOffDayModal = () => {
    setOffDayModal({ open: false, edit: null });
  };

  const saveOffDay = async () => {
    if (!sessionId || !offDayForm.date) return;
    setError('');
    try {
      const payload = {
        session: Number(sessionId),
        date: offDayForm.date,
        reason: offDayForm.reason || '',
        status: offDayForm.status,
        dismissal_time: offDayForm.dismissal_time || null,
      };
      if (offDayModal.edit) {
        await sessionAPI.updateOffDay(offDayModal.edit.id, payload);
        setSession((prev) => ({
          ...prev,
          off_days: (prev?.off_days ?? []).map((d) => (d.id === offDayModal.edit.id ? { ...d, ...payload } : d)),
        }));
      } else {
        const res = await sessionAPI.createOffDay(payload);
        setSession((prev) => ({
          ...prev,
          off_days: [...(prev?.off_days ?? []), res.data],
        }));
      }
      closeOffDayModal();
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message);
    }
  };

  const deleteOffDay = async (id) => {
    if (!window.confirm('Remove this off day?')) return;
    setError('');
    try {
      await sessionAPI.deleteOffDay(id);
      setSession((prev) => ({
        ...prev,
        off_days: (prev?.off_days ?? []).filter((d) => d.id !== id),
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handleScheduleChange = async (row, field, value) => {
    if (!session) return;
    const payload = { ...row, [field]: value };
    try {
      await sessionAPI.updateDailySchedule(row.id, payload);
      setSession((prev) => ({
        ...prev,
        daily_schedules: (prev?.daily_schedules ?? []).map((s) =>
          s.id === row.id ? { ...s, [field]: value } : s
        ),
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const addScheduleRow = async (dayOfWeek) => {
    if (!sessionId) return;
    try {
      const res = await sessionAPI.createDailySchedule({
        session: Number(sessionId),
        day_of_week: dayOfWeek,
        start_time: '09:00',
        end_time: '14:00',
      });
      setSession((prev) => ({
        ...prev,
        daily_schedules: [...(prev?.daily_schedules ?? []), res.data].sort((a, b) => a.day_of_week - b.day_of_week),
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const deleteScheduleRow = async (id) => {
    if (!window.confirm('Remove this day from the schedule?')) return;
    try {
      await sessionAPI.deleteDailySchedule(id);
      setSession((prev) => ({
        ...prev,
        daily_schedules: (prev?.daily_schedules ?? []).filter((s) => s.id !== id),
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const offDays = session?.off_days ?? [];
  const dailySchedules = session?.daily_schedules ?? [];

  const offDayDates = useMemo(
    () => new Set((session?.off_days ?? []).map((o) => o.date)),
    [session?.off_days]
  );

  const holidaysWithStatus = useMemo(() => {
    return (holidays || []).map((h) => ({
      ...h,
      hasOffDay: offDayDates.has(h.dateStr),
    }));
  }, [holidays, offDayDates]);

  const monthsInRange = useMemo(() => {
    if (!session?.start_date || !session?.end_date) return [];
    const start = parseISO(session.start_date);
    const end = parseISO(session.end_date);
    const months = [];
    let d = startOfMonth(start);
    const lastMonth = endOfMonth(end);
    while (isBefore(d, lastMonth) || d.getTime() === lastMonth.getTime()) {
      months.push({ year: d.getFullYear(), month: d.getMonth() });
      d = addDays(endOfMonth(d), 1);
    }
    return months;
  }, [session?.start_date, session?.end_date]);

  return (
    <Box>
      <AppHeader backTo="/" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          School Session Setup (Parent Packet)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set first/last day of school, off days, and daily schedule per location. Required before sending the parent packet.
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={siteId}
                label="Location"
                onChange={(e) => setSiteId(e.target.value)}
              >
                <MenuItem value="">Select location</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.name || site.slug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Session</InputLabel>
              <Select
                value={sessionId}
                label="Session"
                onChange={(e) => setSessionId(e.target.value)}
                disabled={!siteId}
              >
                <MenuItem value="">Select session</MenuItem>
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.display_name || s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {loadingSession && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loadingSession && session && (
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={8}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Session dates" />
              <Tab label="Off days" />
              <Tab label="Daily schedule" />
            </Tabs>

            {tab === 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  First day and last day of school must be set before the parent packet can be sent.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    label="Session name"
                    value={session.name || ''}
                    onChange={(e) => handleSessionField('name', e.target.value)}
                    sx={{ minWidth: 220 }}
                    helperText="Stored name (display in app uses clean format: SY 2026-2027, Summer 2026, etc.)"
                  />
                  <TextField
                    size="small"
                    label="Start date"
                    type="date"
                    value={session.start_date || ''}
                    onChange={(e) => handleSessionField('start_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    size="small"
                    label="End date"
                    type="date"
                    value={session.end_date || ''}
                    onChange={(e) => handleSessionField('end_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    size="small"
                    label="First day of school *"
                    type="date"
                    value={session.first_day_school || ''}
                    onChange={(e) => handleSessionField('first_day_school', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                    helperText="Required for packet send"
                  />
                  <TextField
                    size="small"
                    label="Last day of school *"
                    type="date"
                    value={session.last_day_school || ''}
                    onChange={(e) => handleSessionField('last_day_school', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                    helperText="Required for packet send; can be updated anytime"
                  />
                  <TextField
                    size="small"
                    label="Back to School Night"
                    type="date"
                    value={session.back_to_school_night_date || ''}
                    onChange={(e) => handleSessionField('back_to_school_night_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    size="small"
                    label="PTA Evening"
                    type="date"
                    value={session.pta_evening_date || ''}
                    onChange={(e) => handleSessionField('pta_evening_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <Button variant="contained" onClick={handleSaveSession} disabled={saving}>
                    {saving ? 'Saving...' : 'Save session'}
                  </Button>
                </Box>
                {sessionDayCount != null && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: sessionDayCount.diff >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {sessionDayCount.total} session days
                      {sessionDayCount.diff > 0 && ` — ${sessionDayCount.diff} over the minimum`}
                      {sessionDayCount.diff < 0 && ` — ${Math.abs(sessionDayCount.diff)} below the minimum`}
                      {sessionDayCount.diff === 0 && ' — exactly at minimum'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Counts Mon–Fri between start and end, minus off days on weekdays. Minimum: {MIN_SESSION_DAYS} days.
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {tab === 1 && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Off days (no sessions, early dismissal, etc.)</Typography>
                  <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => openOffDayModal()}>
                    Add off day
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Dismissal time</TableCell>
                        <TableCell width={80} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {offDays
                        .slice()
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.reason}</TableCell>
                            <TableCell>
                              {OFF_DAY_STATUSES.find((s) => s.value === row.status)?.label ?? row.status}
                            </TableCell>
                            <TableCell>{row.dismissal_time ? formatTime(row.dismissal_time) : '—'}</TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => openOffDayModal(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => deleteOffDay(row.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {offDays.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No off days. Click &quot;Add off day&quot; to add holidays and early dismissals.
                  </Typography>
                )}
              </Paper>
            )}

            {tab === 2 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Regular school day start/end times by day of week.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Day</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell width={80} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DAY_NAMES.map((name, dayOfWeek) => {
                        const row = dailySchedules.find((s) => s.day_of_week === dayOfWeek);
                        return (
                          <TableRow key={row?.id ?? `new-${dayOfWeek}`}>
                            <TableCell>{name}</TableCell>
                            {row ? (
                              <>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="time"
                                    value={toTimeValue(row.start_time)}
                                    onChange={(e) => handleScheduleChange(row, 'start_time', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 300 }}
                                    sx={{ width: 100 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="time"
                                    value={toTimeValue(row.end_time)}
                                    onChange={(e) => handleScheduleChange(row, 'end_time', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 300 }}
                                    sx={{ width: 100 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => deleteScheduleRow(row.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell colSpan={2} />
                                <TableCell>
                                  <Button size="small" onClick={() => addScheduleRow(dayOfWeek)}>
                                    Add
                                  </Button>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, position: 'sticky', top: 16 }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setHolidayPanelOpen((o) => !o)}
                  endIcon={holidayPanelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ justifyContent: 'space-between', mb: 1 }}
                >
                  Jewish holiday reference
                </Button>
                <Collapse in={holidayPanelOpen}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Red = weekday (affects session days); gray = weekend. ✅ = in off days; ⚠️ = not yet added. Data from Hebcal.com.
                  </Typography>
                  {holidaysLoading && (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {holidaysError && !holidaysLoading && (
                    <Typography variant="body2" color="text.secondary">
                      Holiday data unavailable — please check back later.
                    </Typography>
                  )}
                  {!holidaysLoading && !holidaysError && monthsInRange.length > 0 && (
                    <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
                      {monthsInRange.map(({ year, month }) => {
                        const monthStart = startOfMonth(new Date(year, month, 1));
                        const monthEnd = endOfMonth(monthStart);
                        const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                        const firstPad = getDay(monthStart);
                        const cells = [...Array(firstPad).fill(null), ...days];
                        const weeks = [];
                        for (let i = 0; i < cells.length; i += 7) {
                          weeks.push(cells.slice(i, i + 7));
                        }
                        const holidayByDate = {};
                        holidaysWithStatus.forEach((h) => {
                          if (!holidayByDate[h.dateStr]) holidayByDate[h.dateStr] = [];
                          holidayByDate[h.dateStr].push(h);
                        });
                        return (
                          <Box key={`${year}-${month}`} sx={{ mb: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {format(monthStart, 'MMMM yyyy')}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, fontSize: '0.7rem' }}>
                              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, i) => (
                                <Box key={i} sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 600 }}>
                                  {label}
                                </Box>
                              ))}
                              {weeks.flatMap((row) =>
                                row.map((day, i) => {
                                  if (!day) {
                                    return <Box key={`e-${i}`} />;
                                  }
                                  const dateStr = format(day, 'yyyy-MM-dd');
                                  const dayNum = getDate(day);
                                  const list = holidayByDate[dateStr] || [];
                                  const hasWeekday = list.length > 0 && list.some((h) => h.isWeekday);
                                  const hasWeekend = list.length > 0 && !hasWeekday;
                                  // Cell: no background, no border, no label. Only day number color for holidays.
                                  const numberColor = hasWeekday ? '#b71c1c' : hasWeekend ? '#757575' : undefined;
                                  return (
                                    <Box key={dateStr} sx={{ minHeight: 28, p: 0.25, textAlign: 'center' }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: numberColor }}>
                                        {dayNum}
                                      </Typography>
                                    </Box>
                                  );
                                })
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {!holidaysLoading && !holidaysError && monthsInRange.length === 0 && holidays.length === 0 && session?.start_date && (
                    <Typography variant="body2" color="text.secondary">
                      No holidays in this session range.
                    </Typography>
                  )}
                </Collapse>
              </Paper>
            </Grid>
          </Grid>
        )}

        {!loadingSession && !session && siteId && sessions.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Select a session to configure calendar and schedule.
          </Typography>
        )}
        {!loadingSession && !session && siteId && sessions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No sessions for this location. Create one from Manage Sessions.
          </Typography>
        )}
      </Container>

      <Dialog open={offDayModal.open} onClose={closeOffDayModal} maxWidth="sm" fullWidth>
        <DialogTitle>{offDayModal.edit ? 'Edit off day' : 'Add off day'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              size="small"
              label="Date"
              type="date"
              value={offDayForm.date}
              onChange={(e) => setOffDayForm((p) => ({ ...p, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              size="small"
              label="Reason"
              value={offDayForm.reason}
              onChange={(e) => setOffDayForm((p) => ({ ...p, reason: e.target.value }))}
              placeholder="e.g. Rosh Hashanah"
            />
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={offDayForm.status}
                label="Status"
                onChange={(e) => setOffDayForm((p) => ({ ...p, status: e.target.value }))}
              >
                {OFF_DAY_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Dismissal time (optional)"
              type="time"
              value={offDayForm.dismissal_time}
              onChange={(e) => setOffDayForm((p) => ({ ...p, dismissal_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              helperText="For early dismissal days"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOffDayModal}>Cancel</Button>
          <Button variant="contained" onClick={saveOffDay} disabled={!offDayForm.date}>
            {offDayModal.edit ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
